import { queryOptions } from '@tanstack/react-query';

import { fetchReplyDraftsGateway } from './fetchReplyDraftsGateway';
import { openReplyDraftsQueryKey } from '../model/queryKeys';
import type { ReplyDraftsGateway } from '../ports/replyDraftsGateway';

import { FIVE_MINUTES, ONE_MINUTE } from '@/constants/time';
import { userScoped } from '@/util/userScope';

/**
 * The signed-in expert's open drafts. It decides whether `.draft` is offered,
 * so it is refetched after every send in a conversation rather than waiting
 * out its stale time: only the server knows whether a reply sent without the
 * draft discarded it.
 */
export const getReplyDraftsV2QueryOptions = userScoped(
  'user',
  (userId: string | undefined, gateway: ReplyDraftsGateway = fetchReplyDraftsGateway) =>
    queryOptions({
      queryKey: openReplyDraftsQueryKey(userId),
      queryFn: () => gateway.listOpen(),
      staleTime: ONE_MINUTE,
    })
);

/**
 * One draft. Every write resolves to the draft as the server now holds it, and
 * the hooks put that straight into this cache, so it is never refetched mid-edit.
 */
export const getReplyDraftV2QueryOptions = (
  replyDraftId: string,
  gateway: ReplyDraftsGateway = fetchReplyDraftsGateway
) =>
  queryOptions({
    queryKey: ['replyDraftV2', replyDraftId],
    queryFn: () => gateway.get(replyDraftId),
    staleTime: Infinity,
  });

/**
 * A source's catalog record, read when the expert opens its details. Its
 * summary can arrive after the record does, so it goes stale in minutes rather
 * than never, and a reopened source picks the summary up once it is ready.
 */
export const getReplyDraftSourceV2QueryOptions = (
  publicationId: number,
  gateway: ReplyDraftsGateway = fetchReplyDraftsGateway
) =>
  queryOptions({
    queryKey: ['replyDraftSourceV2', publicationId],
    queryFn: () => gateway.getPublication(publicationId),
    staleTime: FIVE_MINUTES,
  });
